import React from 'react';
import { 
  DollarSign, TrendingUp, Calculator, PieChart, Wallet, Target,
  ChevronRight, CheckCircle, AlertTriangle, XCircle, BarChart3, CreditCard
} from 'lucide-react';

/**
 * FinanceFlowMap - Visual mini-map showing the financial flow
 * Receita → Custos → EBITDA → Resultados (with debt and liquidity monitoring)
 * Includes health indicators (green/amber/red traffic lights)
 */

// Health status configuration - based on variance thresholds
const getHealthStatus = (variance, higherIsBetter = true) => {
  if (variance === undefined || variance === null) return 'neutral';
  const adjustedVariance = higherIsBetter ? variance : -variance;
  if (adjustedVariance >= 0) return 'healthy';      // Green - on or above target
  if (adjustedVariance >= -5) return 'attention';   // Amber - slight deviation
  return 'critical';                                 // Red - significant deviation
};

// Financial health data for 2026 (1T) and 2025
const financialHealth = {
  2026: {
    'revenue-analysis': { 
      variance: -5.7,     // Receita 5.7% abaixo
      metric: '94%',
      label: { pt: 'vs Orçamento', en: 'vs Budget' }
    },
    'operating-costs': { 
      variance: 3,        // Custos 3% abaixo do orçamento (bom - custos menores)
      metric: '97%',
      label: { pt: 'vs Orçamento', en: 'vs Budget' },
      higherIsBetter: false
    },
    'ebitda-detail': { 
      variance: -12,      // EBITDA 12% abaixo
      metric: '88%',
      label: { pt: 'vs Orçamento', en: 'vs Budget' }
    },
    'income-statement': { 
      variance: -8,       // Resultado 8% abaixo
      metric: '92%',
      label: { pt: 'Resultado', en: 'Net Income' }
    },
    'debt-financing': { 
      variance: 2,        // Dívida controlada
      metric: '102%',
      label: { pt: 'Cobertura', en: 'Coverage' }
    },
    'liquidity': { 
      variance: 5,        // Liquidez acima do target
      metric: '105%',
      label: { pt: 'Liquidez', en: 'Liquidity' }
    }
  },
  2025: {
    'revenue-analysis': { variance: 4, metric: '104%', label: { pt: 'vs Orçamento', en: 'vs Budget' } },
    'operating-costs': { variance: 2, metric: '98%', label: { pt: 'vs Orçamento', en: 'vs Budget' }, higherIsBetter: false },
    'ebitda-detail': { variance: 5, metric: '105%', label: { pt: 'vs Orçamento', en: 'vs Budget' } },
    'income-statement': { variance: 3, metric: '103%', label: { pt: 'Resultado', en: 'Net Income' } },
    'debt-financing': { variance: 0, metric: '100%', label: { pt: 'Cobertura', en: 'Coverage' } },
    'liquidity': { variance: 8, metric: '108%', label: { pt: 'Liquidez', en: 'Liquidity' } }
  }
};

const FinanceFlowMap = ({ activeSection, setActiveSection, year = 2026, language = 'pt' }) => {
  const is2026 = year === 2026;
  const yearData = financialHealth[year] || financialHealth[2026];

  // Suffix for section IDs based on year
  const suffix = is2026 ? '-2026' : '';

  const flowSteps = [
    {
      id: 'revenue-analysis',
      targetId: is2026 ? 'revenue-detail-2026' : 'revenue-detail',
      icon: DollarSign,
      label: { pt: 'Receita e Mercado', en: 'Revenue & Market' },
      sublabel: null,
      color: 'purple',
      description: { pt: 'Análise de receita', en: 'Revenue analysis' }
    },
    {
      id: 'operating-costs',
      targetId: is2026 ? 'ebitda-detail-2026' : 'ebitda-detail',
      icon: Calculator,
      label: { pt: 'Custos e Margem', en: 'Costs & Margin' },
      sublabel: null,
      color: 'emerald',
      description: { pt: 'Custos e margem', en: 'Costs and margin' }
    },
    {
      id: 'ebitda-detail',
      targetId: is2026 ? 'liquidity-2026' : 'liquidity',
      icon: Wallet,
      label: { pt: 'Caixa e Liquidez', en: 'Cash & Liquidity' },
      sublabel: null,
      color: 'gold',
      description: { pt: 'Caixa e liquidez', en: 'Cash and liquidity' }
    },
    {
      id: 'income-statement',
      targetId: null,
      icon: Target,
      label: { pt: 'Risco e Entrega', en: 'Risk & Delivery' },
      sublabel: null,
      color: 'amber',
      description: { pt: 'Risco e Entrega', en: 'Risk and Delivery' }
    }
  ];

  const supportSteps = [
    {
      id: 'debt-financing',
      targetId: is2026 ? 'debt-2026' : 'debt',
      icon: CreditCard,
      label: { pt: 'Dívida', en: 'Debt' },
      sublabel: { pt: 'Financiamento', en: 'Financing' },
      color: 'purple'
    },
    {
      id: 'liquidity',
      targetId: is2026 ? 'balance-sheet-2026' : 'balance-sheet',
      icon: Wallet,
      label: { pt: 'Estrutura', en: 'Capital' },
      sublabel: { pt: 'de Capital', en: 'Structure' },
      color: 'rose'
    }
  ];

  const getColorClasses = (color, isActive) => {
    const colors = {
      emerald: {
        bg: isActive ? 'bg-emerald-500' : 'bg-emerald-500/20',
        border: isActive ? 'border-emerald-400' : 'border-emerald-500/30',
        text: isActive ? 'text-white' : 'text-emerald-400',
        ring: 'ring-emerald-400/50',
        glow: 'shadow-emerald-500/30'
      },
      amber: {
        bg: isActive ? 'bg-amber-500' : 'bg-amber-500/20',
        border: isActive ? 'border-amber-400' : 'border-amber-500/30',
        text: isActive ? 'text-white' : 'text-amber-400',
        ring: 'ring-amber-400/50',
        glow: 'shadow-amber-500/30'
      },
      blue: {
        bg: isActive ? 'bg-blue-500' : 'bg-blue-500/20',
        border: isActive ? 'border-blue-400' : 'border-blue-500/30',
        text: isActive ? 'text-white' : 'text-blue-400',
        ring: 'ring-blue-400/50',
        glow: 'shadow-blue-500/30'
      },
      gold: {
        bg: isActive ? 'bg-gold-500' : 'bg-gold-500/20',
        border: isActive ? 'border-gold-400' : 'border-gold-500/30',
        text: isActive ? 'text-white' : 'text-gold-400',
        ring: 'ring-gold-400/50',
        glow: 'shadow-gold-500/30'
      },
      purple: {
        bg: isActive ? 'bg-purple-500' : 'bg-purple-500/20',
        border: isActive ? 'border-purple-400' : 'border-purple-500/30',
        text: isActive ? 'text-white' : 'text-purple-400',
        ring: 'ring-purple-400/50',
        glow: 'shadow-purple-500/30'
      },
      rose: {
        bg: isActive ? 'bg-rose-500' : 'bg-rose-500/20',
        border: isActive ? 'border-rose-400' : 'border-rose-500/30',
        text: isActive ? 'text-white' : 'text-rose-400',
        ring: 'ring-rose-400/50',
        glow: 'shadow-rose-500/30'
      }
    };
    return colors[color] || colors.blue;
  };

  // Health indicator component
  const HealthIndicator = ({ status, metric, label }) => {
    const getHealthColors = () => {
      switch (status) {
        case 'healthy':
          return { bg: 'bg-emerald-500', border: 'border-emerald-400', icon: CheckCircle, text: 'text-emerald-400' };
        case 'attention':
          return { bg: 'bg-amber-500', border: 'border-amber-400', icon: AlertTriangle, text: 'text-amber-400' };
        case 'critical':
          return { bg: 'bg-red-500', border: 'border-red-400', icon: XCircle, text: 'text-red-400' };
        default:
          return { bg: 'bg-slate-500', border: 'border-slate-400', icon: null, text: 'text-slate-400' };
      }
    };
    
    const colors = getHealthColors();
    const IconComponent = colors.icon;
    
    return (
      <div className="absolute -bottom-1.5 left-1/2 transform -translate-x-1/2 flex items-center gap-1">
        <div 
          className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full ${colors.bg} border ${colors.border} shadow-sm`}
          title={`${label}: ${metric}`}
        >
          {IconComponent && <IconComponent className="w-2.5 h-2.5 text-white" />}
          <span className="text-[8px] font-bold text-white">{metric}</span>
        </div>
      </div>
    );
  };

  // Check if section matches based on targetId
  const isSectionActive = (stepId) => {
    if (!activeSection) return false;
    // Find the step with this id
    const step = [...flowSteps, ...supportSteps].find(s => s.id === stepId);
    if (!step) return false;
    // Check if activeSection matches targetId
    return activeSection === step.targetId;
  };

  const FlowNode = ({ step, onClick, showArrow = true }) => {
    const Icon = step.icon;
    const isActive = activeSection === step.targetId;
    const colors = getColorClasses(step.color, isActive);
    const healthData = yearData[step.id];
    const healthStatus = healthData ? getHealthStatus(healthData.variance, healthData.higherIsBetter !== false) : 'neutral';
    
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={onClick}
          data-testid={`finance-flow-node-${step.id}`}
          className={`
            relative flex flex-col items-center p-3 pb-4 rounded-xl border-2 transition-all duration-300
            ${colors.bg} ${colors.border}
            ${isActive ? `ring-2 ${colors.ring} shadow-lg ${colors.glow}` : 'hover:scale-105'}
            cursor-pointer min-w-[100px]
          `}
        >
          <Icon className={`w-5 h-5 ${colors.text} mb-1`} />
          <span className={`text-[10px] font-semibold ${colors.text} text-center leading-tight`}>
            {step.label[language]}
          </span>
          {step.sublabel && (
            <span className={`text-[9px] ${isActive ? 'text-white/70' : 'text-slate-400'}`}>
              {step.sublabel[language]}
            </span>
          )}
          
          {/* Active indicator pulse */}
          {isActive && (
            <div className="absolute -top-1 -right-1 w-3 h-3">
              <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
            </div>
          )}
        </button>
        
        {showArrow && (
          <div className="flex items-center text-slate-500">
            <ChevronRight className="w-4 h-4" />
          </div>
        )}
      </div>
    );
  };

  return (
    <div 
      className="bg-gradient-to-r from-[#0A192F] via-[#112240] to-[#0A192F] rounded-2xl p-4 mb-6 shadow-lg"
      data-testid="finance-flow-map"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-xs uppercase tracking-wider">
            {language === 'pt' ? 'Motor da Execução Financeira' : 'Financial Execution Engine'}
          </h3>
          <span className="px-2 py-0.5 bg-gold-500/20 text-gold-400 text-[10px] font-semibold rounded">
            {is2026 ? '1T 2026' : '2025'}
          </span>
        </div>
        <div className="text-slate-500 text-[10px]">
          {language === 'pt' ? 'Clique para navegar' : 'Click to navigate'}
        </div>
      </div>

      {/* Main Flow */}
      <div className="flex items-center justify-center gap-1 mb-3">
        {flowSteps.map((step, index) => (
          <FlowNode
            key={step.id}
            step={step}
            onClick={() => {
              if (step.targetId) {
                setActiveSection(step.targetId);
              } else {
                // Scroll to synthesis section for "Risco e Entrega"
                const element = document.getElementById('sintese-outlook');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }
            }}
            showArrow={index < flowSteps.length - 1}
          />
        ))}
      </div>

      {/* Support Functions Row */}
      <div className="flex items-center justify-center gap-4 pt-3 border-t border-white/10">
        <span className="text-slate-500 text-[10px] uppercase tracking-wider">
          {language === 'pt' ? 'Estrutura Financeira' : 'Financial Structure'}:
        </span>
        {supportSteps.map((step) => {
          const Icon = step.icon;
          const isActive = activeSection === step.targetId;
          const colors = getColorClasses(step.color, isActive);
          
          return (
            <button
              key={step.id}
              onClick={() => setActiveSection(step.targetId)}
              data-testid={`finance-flow-node-${step.id}`}
              className={`
                relative flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all duration-300
                ${colors.bg} ${colors.border}
                ${isActive ? `ring-1 ${colors.ring}` : 'hover:scale-105'}
                cursor-pointer
              `}
            >
              <Icon className={`w-4 h-4 ${colors.text}`} />
              <div className="flex flex-col items-start">
                <span className={`text-[10px] font-semibold ${colors.text}`}>
                  {step.label[language]}
                </span>
                <span className={`text-[9px] ${isActive ? 'text-white/60' : 'text-slate-500'}`}>
                  {step.sublabel[language]}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FinanceFlowMap;
